import type { AH_ToolQuestionPayload, AhToolQuestionOutcome } from '@numeo/types'
import { isAxiosError } from 'axios'
import { create } from 'zustand'
import { getApiErrorMessage } from '@/utils/get-user-error-message'
import { createStreamIdleGuard } from '@/utils/stream-idle-guard'
import { createGeneralChat, fetchGeneralMessages, sendGeneralMessage } from '../api/generalAgentChatApi'
import type { GeneralChatMessage, GeneralChatStatus } from '../types'
import { createQuestionActions } from './toolQuestionActions'
import { pruneForMessage, pruneSettled, questionsOfTab, restoreAfterFailedMessage, type ToolQuestionMap } from './toolQuestions'

// The open chat, kept for this browser tab so a reload finds the same conversation, with the user and company it belongs to.
const TAB_KEY = 'numeo.general-agent.tab'
// A chat is titled by its first message, as the history list shows it.
const TITLE_MAX = 80
const SEND_FAILED = 'Your message was not sent. Please try again.'
const LOAD_FAILED = 'Could not load this chat. Please try again.'
const TURN_FAILED = 'Something went wrong. Please try again.'
const NO_RESPONSE = 'The agent did not respond. Please try again.'
// A running turn sends a heartbeat every 15 s, so this much silence means its events are not reaching us (the same
// window as the mobile chat's stall watchdog). Then one read of the stored messages settles it, as the AI Hub's
// send-ack watchdog does: a stored reply is shown, and no reply means the turn is lost.
const STALL_MS = 45_000

interface SavedChat {
    owner: string
    tabId: string | null
}

function readSaved(): SavedChat | null {
    try {
        const raw = sessionStorage.getItem(TAB_KEY)
        const saved: unknown = raw ? JSON.parse(raw) : null
        if (saved && typeof saved === 'object' && 'owner' in saved && typeof saved.owner === 'string') {
            return { owner: saved.owner, tabId: 'tabId' in saved && typeof saved.tabId === 'string' ? saved.tabId : null }
        }
        return null
    } catch {
        return null // storage blocked (private window) or an old value: start without a saved chat
    }
}

function writeSaved(saved: SavedChat): void {
    try {
        sessionStorage.setItem(TAB_KEY, JSON.stringify(saved))
    } catch {
        // storage blocked: the chat still works, it just isn't found again after a reload
    }
}

/** A chat that is not the user's any more (deleted, another company, the flag turned off): forget it. */
const isGone = (error: unknown) => isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 403)

export interface GeneralAgentChatStore {
    /** Whose chat this is (`<userId>:<projectName>`); a different signed-in user starts clean. */
    owner: string | null
    tabId: string | null
    messages: GeneralChatMessage[]
    state: GeneralChatStatus
    /** The floating panel is open (shared so the full page can minimize into it). */
    panelOpen: boolean
    setPanelOpen: (open: boolean) => void
    /** What the panel and the page show: the conversation or the list of past chats. */
    view: 'chat' | 'history'
    setView: (view: 'chat' | 'history') => void
    /** Ties the chat to the signed-in user; called on every render of the launcher. */
    bindOwner: (owner: string) => void
    /** Opens a past chat from the history list. */
    openChat: (tabId: string) => Promise<void>
    /** Loads the saved conversation once, after a reload. */
    restore: () => Promise<void>
    send: (text: string) => Promise<void>
    /** Starts a new conversation. */
    reset: () => void
    /** The run on `tabId` failed (`AH_Error`). */
    turnFailed: (tabId: string, message?: string) => void
    /** The run on `tabId` finished (`AH_StreamEnd`): read its reply. */
    turnEnded: (tabId: string) => void
    /** Any other event of the run on `tabId` (its start, a heartbeat): the turn is alive. */
    turnActive: (tabId: string) => void
    /** The open chat's card questions (`AH_ToolQuestion`), by `questionId`. */
    pendingQuestions: ToolQuestionMap
    /** A card asked the user something (`AH_ToolQuestion`, live, replayed or read on open): its turn ended waiting. */
    questionAsked: (payload: AH_ToolQuestionPayload) => void
    /** The question stopped waiting (`AH_ToolQuestionResolved`). */
    questionResolved: (questionId: string, outcome: AhToolQuestionOutcome) => void
    /** The user's Yes (`accept`) or No (`decline`): the waiting turn resumes with it. */
    answerQuestion: (questionId: string, action: 'accept' | 'decline') => Promise<void>
    /** The socket reconnected: the open chat's question events may have been missed, so read the waiting one again. */
    resync: () => Promise<void>
    /** What an upload filed, told to the agent (FR-D2) once nothing waits: a message now would withdraw an open question
     *  or supersede the running turn, which the user never chose. */
    heldNotices: string[]
    /** Sends an upload's notice now, or holds it until the turn ends and no question waits. */
    notify: (text: string) => void
}

/** Nothing waits: no running turn and no question of the open chat still open or being answered. */
const canTell = (s: Pick<GeneralAgentChatStore, 'state' | 'pendingQuestions' | 'tabId'>): boolean =>
    s.state.status !== 'waiting' && !questionsOfTab(s.pendingQuestions, s.tabId).some((q) => q.status === 'open' || q.status === 'sending')

let restored = false
// Bumped whenever the screen moves to another conversation or loads one, so a slower answer for an
// earlier one never lands on the chat now open.
let generation = 0
// Bumped only when the screen moves to another conversation. A send checks this one: a reply read meanwhile (the
// end of the turn it supersedes) must not hide that the send itself failed.
let screen = 0

/**
 * The general agent's one conversation, shared by the floating panel and the full page. A turn's end and failure come
 * only from the `AH_*` socket events (`handleGeneralAgentSocketEvent`); a turn that goes silent is settled by one read of
 * the stored messages (`STALL_MS`).
 */
export const useGeneralAgentChatStore = create<GeneralAgentChatStore>((set, get) => {
    const save = (tabId: string | null) => {
        const owner = get().owner
        if (owner) writeSaved({ owner, tabId })
    }

    /** Reads `tabId`'s bubbles and shows them, unless the user has moved on meanwhile. */
    const load = async (tabId: string): Promise<boolean> => {
        const mine = ++generation
        const rows = await fetchGeneralMessages(tabId)
        if (mine !== generation || get().tabId !== tabId) return false
        set({ messages: rows })
        return true
    }

    /** Ends the wait: the reply is shown, or the turn failed. */
    const settle = (state: GeneralChatStatus) => {
        watchdog.cancel()
        set({ state })
    }

    /** The turn went silent: read the stored messages once. A reply after the user's message ends the wait. A turn that
     *  ended on a card question shows it, though its live event never came (on stage a hard-reloaded page missed it and
     *  told the user the agent did not respond while its question waited). Neither means the turn is lost, and the user is
     *  told so instead of waiting forever. */
    const stalled = () => {
        const { tabId, state } = get()
        if (!tabId || state.status !== 'waiting') return
        load(tabId)
            .then(async (shown) => {
                if (!shown || get().state.status !== 'waiting') return
                if (get().messages.at(-1)?.message_type === 'agent-response') return settle({ status: 'ready' })
                await readPending(tabId)
                if (get().tabId === tabId && get().state.status === 'waiting') settle({ status: 'error', message: NO_RESPONSE })
            })
            .catch(() => get().tabId === tabId && settle({ status: 'error', message: NO_RESPONSE }))
    }
    const watchdog = createStreamIdleGuard(STALL_MS, stalled)
    const { readPending, ...questionActions } = createQuestionActions({ get, set, settle, watchdog })

    return {
        owner: null,
        tabId: null,
        messages: [],
        state: { status: 'ready' },
        pendingQuestions: {},
        heldNotices: [],
        panelOpen: false,
        setPanelOpen: (panelOpen) => set({ panelOpen }),
        view: 'chat',
        setView: (view) => set({ view }),

        bindOwner: (owner) => {
            if (get().owner === owner) return
            generation++
            screen++
            restored = false
            const saved = readSaved()
            const tabId = saved?.owner === owner ? saved.tabId : null
            writeSaved({ owner, tabId })
            watchdog.cancel()
            set({ owner, tabId, messages: [], pendingQuestions: {}, heldNotices: [], state: { status: 'ready' }, panelOpen: false, view: 'chat' })
        },

        openChat: async (tabId) => {
            screen++
            watchdog.cancel()
            save(tabId)
            set({ tabId, messages: [], pendingQuestions: {}, heldNotices: [], state: { status: 'ready' }, view: 'chat' })
            try {
                if (await load(tabId)) await readPending(tabId)
            } catch (error) {
                if (get().tabId === tabId) set({ state: { status: 'error', message: getApiErrorMessage(error, LOAD_FAILED) } })
            }
        },

        restore: async () => {
            const { tabId, messages } = get()
            if (restored || !tabId || messages.length) return
            restored = true
            try {
                if (await load(tabId)) await readPending(tabId)
            } catch (error) {
                if (get().tabId !== tabId) return
                if (isGone(error)) {
                    save(null)
                    set({ tabId: null })
                } else set({ state: { status: 'error', message: getApiErrorMessage(error, LOAD_FAILED) } })
            }
        },

        send: async (text) => {
            const typed = text.trim()
            // A message while a turn runs supersedes it (ADR-048), so the user is never stuck behind a turn that stopped.
            if (!typed) return
            // The user chose to send now: any upload notice still held goes with it.
            const message = [...get().heldNotices, typed].join('\n')
            if (get().heldNotices.length) set({ heldNotices: [] })
            // A read already in flight predates this message: it must not replace the bubble just added.
            generation++
            const sendScreen = screen
            set((s) => ({
                // The server withdraws a question the message leaves open; answered and expired ones are behind it now.
                pendingQuestions: s.tabId ? pruneForMessage(s.pendingQuestions, s.tabId) : s.pendingQuestions,
                messages: [...s.messages, { id: `local-${Date.now()}`, message_type: 'user', content: message, created_at: new Date().toISOString() }],
                state: { status: 'waiting' },
            }))
            try {
                let tabId = get().tabId
                if (!tabId) {
                    tabId = await createGeneralChat(message.slice(0, TITLE_MAX))
                    // The user moved to another chat while this one was being created: leave the screen to them.
                    if (sendScreen !== screen) return
                    save(tabId)
                    set({ tabId })
                }
                await sendGeneralMessage(tabId, message)
                if (sendScreen === screen && get().state.status === 'waiting') watchdog.reset()
            } catch (error) {
                if (sendScreen === screen) {
                    // The server never saw the message: it still waits on the open questions, so their Yes and No work again.
                    const open = get().tabId
                    if (open) set((s) => ({ pendingQuestions: restoreAfterFailedMessage(s.pendingQuestions, open) }))
                    settle({ status: 'error', message: getApiErrorMessage(error, SEND_FAILED) })
                }
            }
        },

        reset: () => {
            generation++
            screen++
            watchdog.cancel()
            save(null)
            set({ tabId: null, messages: [], pendingQuestions: {}, heldNotices: [], state: { status: 'ready' }, view: 'chat' })
        },

        turnFailed: (tabId, message) => {
            if (get().tabId !== tabId) return
            generation++
            settle({ status: 'error', message: message || TURN_FAILED })
        },

        turnActive: (tabId) => {
            if (get().tabId === tabId && get().state.status === 'waiting') watchdog.reset()
        },

        turnEnded: (tabId) => {
            if (get().tabId !== tabId) return
            load(tabId)
                .then((shown) => {
                    // The end of a turn the user has since superseded leaves their newer message last: keep waiting for its reply.
                    if (shown && get().state.status === 'waiting' && get().messages.at(-1)?.message_type === 'agent-response') {
                        settle({ status: 'ready' })
                        // The reply says what an answered card did, so its card goes.
                        set((s) => ({ pendingQuestions: pruneSettled(s.pendingQuestions, tabId) }))
                    }
                })
                .catch((error: unknown) => {
                    if (get().tabId === tabId) settle({ status: 'error', message: getApiErrorMessage(error, LOAD_FAILED) })
                })
        },

        ...questionActions,

        notify: (text) => (canTell(get()) ? void get().send(text) : set((s) => ({ heldNotices: [...s.heldNotices, text] }))),
    }
})

// A held upload notice goes out as soon as nothing waits (the turn ended, the question was answered or stopped waiting).
useGeneralAgentChatStore.subscribe((s) => {
    if (!s.heldNotices.length || !canTell(s)) return
    const text = s.heldNotices.join('\n')
    useGeneralAgentChatStore.setState({ heldNotices: [] })
    void s.send(text)
})
